// frontend/src/POInbox.jsx
// Procurement Inbox — inbound vendor email replies for procurement@brewout.de
import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import {
  Alert, Box, Stack, Typography, Chip, Button, TextField, Autocomplete,
  Table, TableHead, TableBody, TableRow, TableCell,
  CircularProgress, Collapse, Checkbox, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  useTheme, useMediaQuery,
} from '@mui/material'
import SyncIcon         from '@mui/icons-material/Sync'
import LinkIcon         from '@mui/icons-material/Link'
import BlockIcon        from '@mui/icons-material/Block'
import ReplayIcon       from '@mui/icons-material/Replay'
import DoneAllIcon      from '@mui/icons-material/DoneAll'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import PageContainer    from './components/common/PageContainer'
import DisabledReason   from './components/common/DisabledReason'
import { labelFrom, UNMAPPED_LABEL_HINT } from './components/common/labelFrom'
import ViewHeader       from './components/ViewHeader'
import TableFooterSimple from './components/common/TableFooterSimple'
import RowActionsMenu   from './components/common/RowActionsMenu'
import ConfidenceBadge  from './rma/components/ConfidenceBadge'
import EmptyState       from './components/common/EmptyState'
import TriageLine       from './rma/components/TriageLine'
import { toast } from 'sonner'
import UnderlineTabs    from './components/common/UnderlineTabs'
import Panel            from './components/common/Panel'
// 🔴 G4b: Z1 der Liste — Suche · Trefferzaehler · ⋮ Seitenaktionen — und der
// URL-Zustand kommen aus denselben Bausteinen wie auf `/rma/inbox` (G4a).
import ListToolbar      from './components/common/ListToolbar'
import useListQuery     from './components/common/listQuery/useListQuery'
import BulkActionBar    from './components/common/BulkActionBar'
import useRowSelection  from './components/common/useRowSelection'
// 🔴 G4b §2.6/A4: die gemeinsame Inbox-Karte (dieselbe Variante wie `/rma/inbox`).
import MobileRecordCard from './components/common/MobileRecordCard'
import POInboxSkeleton  from './Skeletons/POInboxSkeleton'
import {
  FS, SP, PANEL_RADIUS, RAIL_WIDTH, BEDIENBREITE,
  FIELD_HEIGHT_SX,
  hoverOnly,
} from './theme/tokens'
// 🔴 Der EINE Wischbaustein des Hauses (UI-Durchgang 2 Teil 5, M2) — kein
// zweites Wischsystem (G1–G15, `docs/knowledge/design-language/pattern-library.md`).
import { useSwipeRow } from './hooks/useSwipeRow'
import {
  naechsteAufdeckung, PO_WISCH_FUEHRT_AUS,
  WISCH_FELD_SICHTBAR_AB, WISCH_BEWEGUNG_SX,
} from './inbox/wischRuecknahme'
// 🔴 `v2Tone` — der Aufloeser der v2-Textskala. Der Triage-Grund dieser Karte
// steht als Schrift in der Aktionszeile und braucht 4,5:1 (WCAG 1.4.3); die
// Palettenschluessel liefern Flaechenfarben darunter (R16 B1, s. `TRIAGE_TON`).
import { v2Tone } from './theme/theme'

const API = process.env.REACT_APP_API_BASE_URL

/**
 * Die EINE Quelle der Spaltenbreiten der Desktop-Tabelle (G4b-Nachbesserung,
 * R15 B1 + P33).
 *
 * 🔴 Warum eine Konstante und nicht zwei Literale: Kopfzeile UND Datenzelle
 * setzen dieselbe Breite. Bis zur Nachbesserung trug der Kopf `BEDIENBREITE`
 * (48/140), die Datenzelle aber die alten Literale (40/168) — in der
 * Auto-Tabellenbreite gewinnt das MAXIMUM, die zugesagte 140 war damit
 * wirkungslos (R15 B1). Und weil der Leerzustand eine `colSpan`-Zeile ohne
 * Inhalte rendert, verteilte der Browser die Spalten dort neu; die PO-Spalte
 * sprang um 5 px (P33).
 *
 * 🔴 `subject` traegt ABSICHTLICH keine Breite: mit `table-layout: fixed` nimmt
 * die einzige Spalte ohne Breite den Rest auf — in BEIDEN Zustaenden derselbe
 * Rest, weil alle anderen fest sind. Dieselbe Bauform wie
 * `rma/rmaInboxColumns.js` (Hausform, Welle-2-Befund 1).
 */
const PO_INBOX_COL = {
  select: BEDIENBREITE.auswahl,   // 48 — MUI's `paddingCheckbox`-Regel wird ueberstimmt
  time: 80,
  from: 160,
  po: 110,
  intent: 160,
  action: 120,
  actions: BEDIENBREITE.aktion,   // 140
}

/**
 * Die Auswahlzelle MUSS ihren Token auch RENDEREN.
 *
 * MUI traegt fuer `TableCell padding="checkbox"` die Zwei-Klassen-Regel
 * `&.MuiTableCell-paddingCheckbox { width: 24 }`; eine `sx`-Breite ist nur EINE
 * Klasse und verliert (Akte
 * `docs/backlog-auto-repair/2026-10-13-auswahlspalte-48-wirkt-nicht-mui-24.md`).
 * Der Selektor hier hat dieselbe Spezifitaet und steht in der `sx`, die nach
 * MUI eingespielt wird. Die Jest-Probe
 * `POInbox.spaltenbreite.test.jsx` pinnt die berechnete Breite (48 px).
 */
const AUSWAHLZELLE_SX = {
  width: PO_INBOX_COL.select,
  '&.MuiTableCell-paddingCheckbox': { width: PO_INBOX_COL.select },
}

// The threshold the poller applies before it writes a confirmed delivery date
// onto the PO itself — `backend/purchase_orders/inbox_poller.py:313`
// (`_AUTO_CONFIDENCE_THRESHOLD = 0.75`). Below it the classification is shown
// but the PO is left alone, and nothing on this surface said so.
const PO_AUTO_APPLY_CONFIDENCE = 0.75

const INTENT_LABEL = {
  vendor_confirmation: 'Confirmed',
  vendor_rejection:    'Rejected',
  vendor_question:     'Question',
  vendor_delay_notice: 'Delay Notice',
  other:               'Other',
}
const INTENT_COLOR = {
  vendor_confirmation: 'success',
  vendor_rejection:    'error',
  vendor_question:     'warning',
  vendor_delay_notice: 'warning',
}
const ACTION_LABEL = {
  auto_handled: 'Auto-handled',
  matched:      'Matched',
  unmatched:    'Unmatched',
  ignored:      'Ignored',
  pending:      'Pending',
}
const ACTION_COLOR = {
  auto_handled: 'success',
  matched:      'info',
  unmatched:    'warning',
  ignored:      'default',
  pending:      'warning',
}
const LANG_LABEL = { de: '🇩🇪 DE', en: '🇬🇧 EN', other: '🌐' }

// Every `action_taken` the poller and the two routes actually write:
// `inbox_poller.py:649,725` (auto_handled|matched|unmatched), `routes.py:2926`
// (matched), `routes.py:2965` (ignored), `routes.py:2998` (auto_handled) and
// the model default `pending` (`models.py` PoInboundEmail.action_taken).
// There is no `error` value in the PO domain — the concept asks for an error
// tab "and a PO equivalent"; the equivalent does not exist, so there is none.
// 🔴 `UNMAPPED_LABEL_HINT` und `labelFrom` standen hier als lokale Kopie. Sie
// liegen jetzt in `components/common/labelFrom.js`, weil `EinordnungCard` keinen
// Uebersetzer hatte und die Rohwerte des Modells zeigte (Regel-16-Befund B-1).
// Die Begruendung der Kennzeichnung (§5.4(a)) steht dort.

/**
 * poTriageReason — "why is this row in my queue", from data already on the row.
 *
 * Only where it DISTINGUISHES (the TriageLine contract): the unmatched case is
 * carried by the tab and the object cell, so it is not repeated here.
 */
function poTriageReason(email) {
  if (email.action_taken === 'ignored') return null
  if (email.llm_confidence != null && email.llm_confidence < PO_AUTO_APPLY_CONFIDENCE) {
    return {
      key: 'po_below_auto_apply',
      tone: 'warn',
      text: 'Below the auto-apply threshold — the delivery date was not written to the PO.',
      detail: `The model read this mail, but its confidence sits below ${PO_AUTO_APPLY_CONFIDENCE}, the threshold at which a confirmed delivery date is applied to the purchase order automatically. Check the date yourself before trusting it.`,
    }
  }
  return null
}

/**
 * Die Ton-Farbe des Triage-Grundes auf der TELEFONKARTE — fuer den TEXT.
 *
 * 🔴 R16 B1: hier kommt der Ton aus `v2Tone`, NICHT aus
 * `theme.palette.<ton>.main`. Die Palettenschluessel sind Flaechenfarben:
 * `warning.main` erreicht auf hellem Papier 3,113:1 (gemessen in
 * `docs/knowledge/design-language/pattern-library.md`) — eine
 * `variant="caption"`-Zeile ist Normaltext und braucht 4,5:1 (WCAG 1.4.3).
 * `v2Tone` liefert die gedaempften TEXTwerte derselben Skala
 * (`theme/theme.js:134-155`) und ist derselbe Aufloeser, den
 * `components/common/menueFarbe.js` dafuer benutzt.
 */
const TRIAGE_TON = (theme) => ({
  warn: v2Tone(theme, 'warn').main,
  err: v2Tone(theme, 'err').main,
  ok: v2Tone(theme, 'ok').main,
  info: v2Tone(theme, 'info').main,
})

function absoluteTime(ts) {
  if (!ts) return ''
  const d = new Date(ts)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('de-DE')
}

/**
 * One body-box style for BOTH renderers. The desktop panel and the mobile card
 * had drifted apart (the card had no body at all); sharing the object keeps them
 * together — and keeps the file's raw `borderRadius` count from growing, which
 * the token gate refuses (Regel 22, ratchet).
 */
const EMAIL_BODY_SX = {
  p: SP.sm, bgcolor: 'background.paper', borderRadius: 1,
  border: '1px solid', borderColor: 'divider',
  maxHeight: 220, overflow: 'auto',
  fontFamily: 'monospace', fontSize: FS.sm,
  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
}

// 🔴 G4b (§2.3/§3.1, ENTSCHEID F3/B3 — dieselbe Form wie G4a): die Reiter tragen
// STABILE SCHLUESSEL (`tab` in der Adresse ist der Schluessel, nie ein Index,
// §10.1), und `all` steht VORN. Bis G4b stand `unread` zuerst und der
// Grundzustand war damit der Ungelesen-Reiter; Konzept `tabellen-filter-suche.md:81`
// verlangt „Grundzustand = … erster Reiter", und der Grundzustand ist `all`.
// 🔴 `all` ist die OBERMENGE AUSSER `archived` — die uebrigen sind Schnitte
// daraus (§2.3.0). Der Satz steht auch in der Ansicht (Caption an der Reiterreihe).
// 🔴 Der PO-Abruf ist `filter`-basiert: ein FEHLENDES `filter` liefert
// `unmatched` (`backend/purchase_orders/routes.py:3482`), NICHT „all". Deshalb
// sendet jeder Abruf `filter` MIT, auch der fuer „All" (`filter=''`).
const TABS = [
  { key: 'all',          label: 'All',                filter: '',             archived: false },
  // Client-seitig ueber der `all`-Menge; der Server kennt keinen Lesestand
  // (`localStorage` `po_inbox_seen`). Der Reitertext sagt die Einschraenkung:
  // der Zaehler zaehlt „ungelesen innerhalb der geladenen Seite" (§2.3.1).
  { key: 'unread',       label: 'Unread (this page)', filter: '',             archived: false, clientFilter: 'unread' },
  { key: 'unmatched',    label: 'Unmatched',          filter: 'unmatched',    archived: false },
  { key: 'auto-handled', label: 'Auto-handled',       filter: 'auto_handled', archived: false },
  { key: 'archived',     label: 'Archived',           filter: 'archived',     archived: true  },
]
const TAB_KEYS = TABS.map(t => t.key)
const TAB_INDEX = Object.fromEntries(TAB_KEYS.map((k, i) => [k, i]))
const tabFuer = (key) => TABS[TAB_INDEX[key]] || TABS[0]
// 🔴 P30 (§5.4): der Praefix der Panel-Verdrahtung (`${praefix}-tab-<i>` /
// `${praefix}-panel-<i>`). EINE Konstante, damit die zwei Seiten nicht
// auseinanderlaufen.
const REITER_PANEL_ID = 'po-inbox'
// 🔴 G4b (§2.3): die ZAEHL-ABRUFE der vier SERVERSEITIGEN Reiter — je einer,
// `limit=1`, nur `total` gelesen. EINMAL je Aufbau (und beim Auffrischen), NICHT
// je Reiterwechsel: es ist der GRUNDBESTAND ohne Suche.
// 🔴 `filter` wird IMMER gesendet (B4): fehlt es, liefert die Route `unmatched`
// statt „all" — eine plausibel aussehende, falsche Zahl. PO braucht KEINEN
// `paramsSerializer` (`filter` ist ein Skalar; das `action`-Array gibt es hier
// nicht).
const ZAEHL_ABRUFE = [
  { key: 'all',          params: { filter: '',             limit: 1 } },
  { key: 'unmatched',    params: { filter: 'unmatched',    limit: 1 } },
  { key: 'auto-handled', params: { filter: 'auto_handled', limit: 1 } },
  { key: 'archived',     params: { filter: 'archived',     limit: 1 } },
]
// 🔴 Leerzustand-Texte JE REITER (Regel 22 Nr. 5, §2.7 — „je Reiter mit eigener
// Erwartung"). Jeder Reiter nennt seinen AUSSCHNITT und WANN dort etwas
// erscheint; `reason` ist Pflicht (`EmptyState` wirft ohne ihn).
const LEER_JE_REITER = {
  all:              { message: 'No messages yet.',                    detail: 'Incoming mail will appear here.' },
  unread:           { message: 'No unread messages on this page.',    detail: 'Mail you have not read on this device appears here.' },
  unmatched:        { message: 'No messages in “Unmatched”.',         detail: 'Mail that could not be matched to a purchase order appears here.' },
  'auto-handled':   { message: 'No messages in “Auto-handled”.',      detail: 'Mail the system handled on its own appears here.' },
  archived:         { message: 'No messages in “Archived”.',          detail: 'Mail you archive appears here.' },
}
function leerzustandFuer(tabKey) {
  const satz = LEER_JE_REITER[tabKey] || LEER_JE_REITER.all
  return { reason: 'no-data', message: satz.message, detail: satz.detail }
}

function formatRelative(ts) {
  if (!ts) return ''
  const d    = new Date(ts)
  const diff = Date.now() - d
  if (diff < 3_600_000)   return `${Math.floor(diff / 60_000)}m ago`
  if (diff < 86_400_000)  return `${Math.floor(diff / 3_600_000)}h ago`
  if (diff < 604_800_000) return `${Math.floor(diff / 86_400_000)}d ago`
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })
}

// ── Link-to-PO control ────────────────────────────────────────────────────────
// ONE definition, two homes: inline in the desktop row's action strip and inside
// the mobile card's dialog. The mobile card previously had no way to link at
// all — that was the mobile triage gap this work item names.

const emptyLinkState = (email) => ({ poId: email.po_id || '', success: false, err: '', saving: false })

/**
 * The picker is CONTROLLED on purpose. The expanded panel is a `<Collapse>`
 * with `unmountOnExit`, so state living in here dies the moment the row is
 * collapsed — a half-typed selection would be thrown away. The state therefore
 * lives in the row (which outlives the panel) and the picker only renders it.
 */
function PoLinkPicker({ email, link, setLink, poOptions, poLoading, poError, onRetryOptions, onLinked, onCancel }) {
  const { poId, success, err, saving } = link
  const patch = (p) => setLink(prev => ({ ...prev, ...p }))

  const submit = async () => {
    if (!poId) { patch({ err: 'Select a PO' }); return }
    patch({ err: '', saving: true })
    try {
      await axios.post(`${API}/api/purchase_orders/inbox/${email.id}/link`, { po_id: poId })
      patch({ success: true, saving: false })
      onLinked?.(email.id, poId)
    } catch (e) {
      patch({ err: e.response?.data?.error || 'Failed to link', saving: false })
    }
  }

  // §5.3 — a failed option fetch used to leave an empty dropdown that looked
  // exactly like "no PO matches your search".
  if (poError) {
    return (
      <Stack direction="row" spacing={SP.chip} alignItems="center">
        <Typography variant="caption" color="error.main">PO list unavailable.</Typography>
        <Button size="small" variant="outlined" onClick={onRetryOptions}>Try again</Button>
        <Button size="small" onClick={onCancel}>Cancel</Button>
      </Stack>
    )
  }

  return (
    <Stack direction="row" spacing={SP.chip} alignItems="flex-start">
      <Autocomplete
        size="small" openOnFocus
        options={poOptions} loading={poLoading}
        value={poOptions.find(o => o.po_id === poId) || null}
        onChange={(_, v) => patch({ poId: v?.po_id || '', success: false })}
        getOptionLabel={o => o.label}
        isOptionEqualToValue={(o, v) => o.po_id === v.po_id}
        renderInput={params => (
          <TextField {...params} placeholder="Select PO…"
            error={!!err} helperText={err} />
        )}
        sx={{ minWidth: 260 }}
      />
      <DisabledReason
        reason={saving
          ? null
          : !poId
            ? 'Select a purchase order first.'
            : success ? 'This inquiry is already linked.' : null}>
        <Button size="small" variant="outlined"
          disabled={!poId || success || saving}
          onClick={submit}>
          {success ? 'Linked!' : 'Link'}
        </Button>
      </DisabledReason>
      <Button size="small" onClick={onCancel}>Cancel</Button>
    </Stack>
  )
}

// ── Desktop table row ─────────────────────────────────────────────────────────

function InboxTableRow({
  email, isSelected, onToggleSelect, selectionActive,
  onIgnore, onReprocess, poOptions, poLoading, poError, onRetryOptions,
  processingId, isSeen, onSeen, onLinked,
}) {
  const navigate    = useNavigate()
  const [expanded, setExpanded]       = useState(false)
  const [linking, setLinking]         = useState(false)
  // Lives here, not in the picker: the panel unmounts on collapse (see the
  // comment on PoLinkPicker), this row does not.
  const [link, setLink]               = useState(() => emptyLinkState(email))

  const isProcessing = processingId === email.id

  const handleRowClick = () => {
    if (selectionActive) { onToggleSelect(email.id); return }
    setExpanded(e => !e)
    if (!isSeen) onSeen?.(email.id)
  }

  // 🔴 G4b-Nachbesserung 3 (R16 W4): die GANZE 48-px-Zelle ist die
  // Trefferflaeche, nicht nur das Kaestchen. Das Kaestchen selbst schaltet
  // ueber seinen `onChange` um — ein zweiter Weg hier wuerde DOPPELT
  // umschalten. MUI legt sein Eingabefeld deckend ueber das ganze Kaestchen,
  // deshalb trifft jeder Klick darauf das `<input>`.
  const handleAuswahlZelle = (e) => {
    e.stopPropagation()
    if (e.target.closest('input[type="checkbox"]')) return
    onToggleSelect(email.id)
  }

  const openLink = (e) => {
    e?.stopPropagation()
    setExpanded(true)
    setLinking(true)
  }

  const extracted = email.llm_extracted_json || {}
  const action    = labelFrom(ACTION_LABEL, email.action_taken)
  const intent    = labelFrom(INTENT_LABEL, email.llm_intent)
  const triage    = poTriageReason(email)
  // 🔴 G4b §2.4: die KENNUNG der Zeile, wie sie die PO-Spalte zeigt — die
  // PO-Nummer, sonst (unverknuepft) „no candidate". Der ⋮-Name traegt sie, und
  // `messeP19` vergleicht genau gegen diesen Zellentext (Spalte 4, 0-basiert).
  const kennung   = email.po_id || 'no candidate'
  // §2.2(8) — the common outcome differs by population and is computed, not fixed.
  const primary   = !email.po_id ? 'link'
    : (email.action_taken === 'pending' || !email.action_taken) ? 'reprocess'
    : null

  return (
    <>
      <TableRow
        hover
        sx={{
          cursor: 'pointer',
          bgcolor: isSelected
            ? 'primary.50'
            : expanded ? 'action.selected'
            : !isSeen ? 'action.hover'
            : undefined,
          opacity: isSeen && !expanded && !isSelected ? 0.75 : 1,
          transition: 'background-color 0.15s, opacity 0.2s',
          '&:hover .inbox-row-checkbox': { opacity: 1 },
        }}
        onClick={handleRowClick}
      >
        <TableCell padding="checkbox" onClick={handleAuswahlZelle} sx={AUSWAHLZELLE_SX}>
          <Box
            className="inbox-row-checkbox"
            sx={{ opacity: isSelected || selectionActive ? 1 : 0, transition: 'opacity 0.15s' }}
          >
            <Checkbox size="small" checked={isSelected}
              onChange={() => onToggleSelect(email.id)} sx={{ p: 0.5 }} />
          </Box>
        </TableCell>

        <TableCell sx={{ whiteSpace: 'nowrap', width: PO_INBOX_COL.time }}>
          <Tooltip title={absoluteTime(email.received_at)}>
            <Typography variant="caption" component="span">
              {formatRelative(email.received_at)}
            </Typography>
          </Tooltip>
        </TableCell>

        <TableCell sx={{ width: PO_INBOX_COL.from }}>
          <Typography variant="body2" noWrap sx={{ maxWidth: 160, fontWeight: isSeen ? 400 : 600 }}>
            {email.from_name || email.from_email}
          </Typography>
          {email.from_name && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 160, display: 'block' }}>
              {email.from_email}
            </Typography>
          )}
        </TableCell>

        <TableCell>
          {/* §2.2(2) — two clamped lines instead of one, so the summary is readable. */}
          <Typography variant="body2" sx={{
            maxWidth: 300, fontWeight: isSeen ? 400 : 600,
            display: '-webkit-box', WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2, overflow: 'hidden',
          }}>
            {email.subject || '(no subject)'}
          </Typography>
          {email.llm_summary && (
            <Typography variant="caption" color="text.secondary" sx={{
              maxWidth: 300, display: '-webkit-box', WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2, overflow: 'hidden',
            }}>
              {email.llm_summary}
            </Typography>
          )}
        </TableCell>

        <TableCell sx={{ width: PO_INBOX_COL.po }}>
          {/* §2.2(3) — an unlinked mail says so instead of showing an em-dash. */}
          {email.po_id ? (
            <Typography
              variant="body2" color="primary"
              sx={{ cursor: 'pointer', textDecoration: 'underline', whiteSpace: 'nowrap' }}
              onClick={e => { e.stopPropagation(); navigate(`/purchase_order/${email.po_id}`) }}
            >
              {email.po_id}
            </Typography>
          ) : (
            <Typography variant="caption" color="text.disabled" sx={{ whiteSpace: 'nowrap' }}>
              no candidate
            </Typography>
          )}
        </TableCell>

        <TableCell sx={{ width: PO_INBOX_COL.intent }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <ConfidenceBadge confidence={email.llm_confidence} intent={email.llm_intent} />
            {email.llm_intent && (
              <Chip
                label={intent.label}
                title={intent.unmapped ? UNMAPPED_LABEL_HINT : undefined}
                color={INTENT_COLOR[email.llm_intent] || 'default'}
                size="small" sx={{ height: 18, fontSize: FS.xxs }}
              />
            )}
          </Stack>
        </TableCell>

        <TableCell sx={{ width: PO_INBOX_COL.action }}>
          <Chip
            label={action.label}
            title={action.unmapped ? UNMAPPED_LABEL_HINT : undefined}
            color={ACTION_COLOR[email.action_taken] || 'default'}
            size="small" variant="outlined"
            sx={{ height: 18, fontSize: FS.xxs, fontStyle: action.unmapped ? 'italic' : undefined }}
          />
        </TableCell>

        <TableCell onClick={e => e.stopPropagation()} sx={{ width: PO_INBOX_COL.actions }}>
          <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.5}>
            {isProcessing ? (
              <CircularProgress size={16} sx={{ mx: 0.5 }} />
            ) : (
              <>
                {/* §2.2(8) — one computed primary action, in the row. */}
                {primary === 'link' && (
                  <Button size="small" variant="contained" startIcon={<LinkIcon />}
                    onClick={openLink}>
                    Link
                  </Button>
                )}
                {primary === 'reprocess' && (
                  <Button size="small" variant="outlined" startIcon={<ReplayIcon />}
                    onClick={e => { e.stopPropagation(); onReprocess(email.id) }}>
                    Reprocess
                  </Button>
                )}
                {/* 🔴 G4b §2.4: der ⋮ traegt den ZEILENBEZUG — `Row actions for
                    <Kennung>`. Der Praefix `Row actions` ist der Selektor der
                    Hausproben (`selektoren.js:189`), die Kennung erfuellt P19. */}
                <RowActionsMenu
                  label={`Row actions for ${kennung}`}
                  actions={[
                    {
                      key: 'reprocess',
                      label: 'Reprocess',
                      icon: <ReplayIcon fontSize="small" />,
                      hidden: !email.po_id || primary === 'reprocess',
                      onClick: () => onReprocess(email.id),
                    },
                    {
                      key: 'ignore',
                      label: 'Mark as ignored',
                      icon: <BlockIcon fontSize="small" />,
                      hidden: email.action_taken === 'ignored',
                      onClick: () => onIgnore(email.id),
                    },
                    {
                      key: 'open',
                      label: 'Open PO',
                      icon: <LinkIcon fontSize="small" />,
                      disabled: !email.po_id,
                      divider: true,
                      onClick: () => navigate(`/purchase_order/${email.po_id}`),
                    },
                  ]}
                />
              </>
            )}
          </Stack>
        </TableCell>
      </TableRow>

      {/* Expanded detail row */}
      <TableRow>
        <TableCell colSpan={8} sx={{ p: 0, border: expanded ? undefined : 'none' }}>
          <Collapse in={expanded && !selectionActive} unmountOnExit>
            <Box sx={{ p: SP.card, bgcolor: 'action.hover' }}>
              <Stack spacing={SP.sm}>

                {/* §2.2(6) — why this row is in the queue, from data already loaded. */}
                {triage && <TriageLine reason={triage} detail={triage.detail} />}

                {/* Summary panel */}
                <Box sx={{
                  p: SP.sm, bgcolor: 'background.paper', borderRadius: 1,
                  border: '1px solid', borderColor: 'divider',
                }}>
                  {(email.detected_language || email.action_taken === 'auto_handled') && (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap mb={SP.sm}>
                      {email.detected_language && LANG_LABEL[email.detected_language] && (
                        <Chip label={LANG_LABEL[email.detected_language]}
                          size="small" variant="outlined" />
                      )}
                      {email.action_taken === 'auto_handled' && (
                        <Chip label="Auto-handled" size="small" color="success" variant="outlined" />
                      )}
                    </Stack>
                  )}
                  {email.llm_summary && (
                    <Typography variant="body2" color="text.secondary" fontStyle="italic" mb={SP.sm}>
                      {email.llm_summary}
                    </Typography>
                  )}
                  <Stack spacing={0.5}>
                    {extracted.confirmed_delivery_date && (
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 96, flexShrink: 0, pt: '1px' }}>
                          Delivery date
                        </Typography>
                        <Typography variant="caption" fontWeight={600}>
                          {extracted.confirmed_delivery_date}
                        </Typography>
                      </Stack>
                    )}
                    {extracted.delay_days && (
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 96, flexShrink: 0, pt: '1px' }}>
                          Delay
                        </Typography>
                        <Typography variant="caption">{extracted.delay_days} days</Typography>
                      </Stack>
                    )}
                    {email.llm_confidence != null && (
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 96, flexShrink: 0, pt: '1px' }}>
                          Confidence
                        </Typography>
                        <Typography variant="caption">{Math.round(email.llm_confidence * 100)}%</Typography>
                      </Stack>
                    )}
                    {!extracted.confirmed_delivery_date && !extracted.delay_days && !email.llm_summary && (
                      <Typography variant="caption" color="text.disabled">No structured data extracted.</Typography>
                    )}
                  </Stack>
                </Box>

                {/* Email body */}
                <Box sx={EMAIL_BODY_SX}>
                  {email.body_text || '(no body)'}
                </Box>

                {/* Action buttons */}
                <Stack direction="row" spacing={SP.sm} flexWrap="wrap" useFlexGap alignItems="center">
                  {linking ? (
                    <PoLinkPicker
                      email={email}
                      link={link} setLink={setLink}
                      poOptions={poOptions} poLoading={poLoading}
                      poError={poError} onRetryOptions={onRetryOptions}
                      onLinked={onLinked}
                      onCancel={() => { setLinking(false); setLink(emptyLinkState(email)) }}
                    />
                  ) : (
                    <Button size="small"
                      variant={primary === 'link' ? 'contained' : 'outlined'}
                      startIcon={<LinkIcon />}
                      onClick={() => setLinking(true)}>
                      {email.po_id ? 'Reassign PO' : 'Link to PO'}
                    </Button>
                  )}

                  {email.po_id && (
                    <Button size="small"
                      variant={primary === 'reprocess' ? 'contained' : 'text'}
                      startIcon={<ReplayIcon />}
                      onClick={() => onReprocess(email.id)} disabled={isProcessing}>
                      Reprocess
                    </Button>
                  )}

                  {/* §2.3(2) — the action that actually clears a row belongs inline,
                      not behind the kebab. In the PO domain "Ignore" IS the archive:
                      the route sets action_taken='ignored' and is_archived=true. */}
                  {email.action_taken !== 'ignored' && (
                    <Button size="small" startIcon={<BlockIcon />} color="inherit"
                      onClick={() => onIgnore(email.id)} disabled={isProcessing}>
                      Ignore &amp; archive
                    </Button>
                  )}
                </Stack>
              </Stack>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

// ── Mobile card ───────────────────────────────────────────────────────────────
//
// 🔴 `WISCH_FELD_SICHTBAR_AB` (G4) und `WISCH_BEWEGUNG_SX` (G8) stehen seit dem
// 2026-10-09 in `frontend/src/inbox/wischRuecknahme.js` — EIN Ort fuer beide
// Karten (R16 W4).

/** Die Breite der aufgedeckten Kachel. Eine Layout-Groesse, kein Anteil: sie
 *  bestimmt zugleich, wie weit der Vordergrund weggleitet (`wischVersatz`), und
 *  beide muessen gleich bleiben, sonst deckt die Kachel nicht auf.
 *
 *  🔴 112 px tragen „Ignore & archive" NUR, weil die Kachel `whiteSpace:
 *  'normal'` setzt (Kachel-`sx` unten). `MuiButton` traegt hausweit
 *  `whiteSpace: 'nowrap'` (`theme/theme.js:556`), und unter `nowrap` waere das
 *  Etikett EIN unumbrechbarer Lauf: die Kachel ist ein `<Button>` mit fester
 *  Breite, sie kann nicht ausweichen, die Zeile laeuft ueber und wird an beiden
 *  Kanten beschnitten (aussen vom `overflow: hidden` des Behaelters, innen vom
 *  deckenden Vordergrund) — genau das Schadensbild, das `theme/theme.js:812-833`
 *  als Defekt aufschreibt (dort gemessen: „sichtbar „d to remove discontinua"").
 *  🔴 Bis zum 2026-10-09 (R16 B1) behauptete dieser Kommentar den zweizeiligen
 *  Umbruch, waehrend KEINE Zeile ihn erzwang — eine Aussage, die ihren
 *  Gegenstand ueberlebt hat. Gemessen wird sie jetzt im Browser:
 *  `frontend/tests/po/po-inbox-mobil.spec.ts`, P.5. */
const PO_WISCH_KACHEL_W = 112

function InboxCard({
  email, onIgnore, onReprocess, poOptions, poLoading, poError, onRetryOptions, onLinked,
  processingId, isSeen, onSeen, auswahl,
}) {
  const navigate    = useNavigate()
  const theme       = useTheme()
  const [expanded, setExpanded] = useState(false)
  const [linkOpen, setLinkOpen] = useState(false)
  const [link, setLink]         = useState(() => emptyLinkState(email))
  const isProcessing = processingId === email.id

  const action = labelFrom(ACTION_LABEL, email.action_taken)
  const intent = labelFrom(INTENT_LABEL, email.llm_intent)
  const triage = poTriageReason(email)
  const extracted = email.llm_extracted_json || {}
  // 🔴 G4b §2.4: die KENNUNG der Karte — dieselbe wie die PO-Spalte der Tabelle.
  const kennung = email.po_id || 'no candidate'
  const primary = !email.po_id ? 'link'
    : (email.action_taken === 'pending' || !email.action_taken) ? 'reprocess'
    : null

  // ── Wischen (UI-Durchgang 2 Teil 5, M3) ──────────────────────────────────
  //
  // 🔴 G7 — hier fuehrt KEIN Wisch aus, und das ist gemessen, nicht gewaehlt:
  // jede der drei Aktionen dieser Karte hat keinen Widerruf.
  //   Ignorieren        `POST /inbox/<id>/ignore`   (`purchase_orders/routes.py:3544`)
  //                     setzt `is_archived` + `action_taken='ignored'` — eine
  //                     Gegenroute gibt es nicht (`grep -n "@bp.route" … | grep inbox`
  //                     liefert nur link/ignore/reprocess/DELETE/bulk-delete).
  //   Neu verarbeiten   `…/reprocess` (`:3526`) — eine Neuanalyse, kein Widerruf.
  //   Verknuepfen       `…/link` (`:3461`) — die einzige Gegenrichtung waere ein
  //                     Loeschen des ganzen Belegs (`DELETE /inbox/<id>`, `:3580`).
  // Also **deckt der Wisch nur auf** (G7 Satz 2): er schiebt die Kachel hervor,
  // ausgefuehrt wird getippt. Die Tabelle steht als Kommentar-Kopf in
  // `frontend/tests/po/po-inbox-mobil.spec.ts`.
  const wischGewischt = useRef(false)
  const [aufgedeckt, setAufgedeckt] = useState(null)
  // 🔴 G7 — hier steht der Ausfuehrungszweig des Wischs, und er ist GEMESSEN
  // leer: `PO_WISCH_FUEHRT_AUS` ist false, weil keine der drei Aktionen dieser
  // Karte eine Gegenrichtung hat. Wer eine ergaenzt (und ihren Widerruf baut),
  // dreht die Konstante um — und dann fuehrt der Wisch aus UND zeigt den
  // sonner-Widerruf im selben Zug.
  const fuehreWischAus = (seite) => {
    if (!PO_WISCH_FUEHRT_AUS) return
    if (seite === 'left') onIgnore(email.id)
    else setLinkOpen(true)
  }
  const { handlers: swipeHandlers, dx, swiping } = useSwipeRow({
    onSwipeLeft: () => {
      wischGewischt.current = true
      setAufgedeckt(s => naechsteAufdeckung(s, 'left'))
      fuehreWischAus('left')
    },
    onSwipeRight: () => {
      wischGewischt.current = true
      setAufgedeckt(s => naechsteAufdeckung(s, 'right'))
      fuehreWischAus('right')
    },
  })
  const wischSeite = aufgedeckt || (dx < 0 ? 'left' : 'right')
  const wischVersatz = aufgedeckt === 'left' ? -PO_WISCH_KACHEL_W
    : aufgedeckt === 'right' ? PO_WISCH_KACHEL_W : dx
  const wischLinks = wischSeite === 'left'
  const triageFarbe = TRIAGE_TON(theme)[triage?.tone] || 'text.secondary'

  // 🔴 G14 (R16 W2) — EINE Handlung, EIN Etikett: dieselbe Beschriftung tragen
  // die Kachel, das Feld, das sie beim Ziehen ankuendigt, UND das ⋯-Menue.
  const etikett = {
    ignore: 'Ignore & archive',
    link: email.po_id ? 'Reassign PO' : 'Link to PO',
  }

  // 🔴 R16 W1 — die Flaeche ist GEFUELLT (Plan §1 G9), aber aus der v2-Skala:
  // `v2Tone(theme, ton).main` als GRUND, modusabhaengige Schrift darauf.
  const wischFlaeche = (ton) => ({
    bgcolor: v2Tone(theme, ton).main,
    color: theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.common.white,
  })

  // 🔴 G4b — der Kartenklick oeffnet (Ruhezustand) bzw. schaltet die Auswahl um
  // (Auswahlmodus, §11.6 „Tippen schaltet um"). G5b-Blocker: ein Gesten-/
  // Auswahl-Spread darf `onClick` nicht ueberschreiben — `gesteFuer` liefert nur
  // Pointer-Handler, dieser Handler bleibt der einzige Klickweg.
  //
  // 🔴 Der Guard auf Bedienelemente ist PFLICHT: die Karte traegt Checkbox,
  // Primaerknopf und ⋮ IN sich; ohne ihn oeffnete jeder Tipp auf sie zusaetzlich
  // die Karte (der Klick blubbert zum Wurzelknoten).
  // 🔴 G5b-Blocker: `auswahl.gesteFuer` liefert ein EIGENES `onClick`, das den
  // Kartenklick überschreiben würde (es schaltet im Auswahlmodus um und
  // verschluckt den Klick nach einem Langdruck). Beide werden deshalb
  // ZUSAMMENGESETZT: erst die Geste (sie darf `preventDefault` setzen), dann der
  // Kartenweg. Der Spread steht VOR `onClick`, sonst gewinnt wieder die Geste.
  const geste = auswahl ? auswahl.gesteFuer(email.id) : null

  /**
   * Die EINE Aufklapp-Handlung. Sie steht getrennt, weil sie ZWEI Eintritte hat:
   * den Titel-Knopf (Tastatur + Screenreader) und die freie Kartenfläche
   * (Zeiger-Komfort, nicht im A11y-Baum — Orchestrator-Entscheid A, 2026-10-18).
   */
  const klappeUm = () => {
    if (aufgedeckt) { setAufgedeckt(null); return }
    if (wischGewischt.current) { wischGewischt.current = false; return }
    setExpanded(v => !v)
    if (!isSeen) onSeen?.(email.id)
  }
  // 🔴 Der Guard gilt NUR fuer den Flächen-Klick: er nimmt Bedienelemente aus,
  // deren eigener Klick sonst ZUSÄTZLICH die Karte aufklappte.
  const handleCardClick = (e) => {
    if (e.target.closest('input, button, a, [role="menuitem"], [role="checkbox"]')) return
    klappeUm()
  }
  const handleKarteKlick = (e) => {
    geste?.onClick?.(e)
    if (e.defaultPrevented) return
    handleCardClick(e)
  }
  // 🔴 Der Titel ist das Bedienelement (Entscheid A): ein echter Knopf mit
  // `aria-expanded`. `stopPropagation` verhindert den ZWEITEN Weg ueber die
  // Flaeche — sonst klappte ein Tipp auf den Titel zweimal.
  const handleTitelKlick = (e) => {
    e.stopPropagation()
    // 🔴 Im AUSWAHLMODUS schaltet auch der Titel um (§11.6) — dieselbe
    // Zusammensetzung wie am Flaechen-Klick.
    geste?.onClick?.(e)
    if (e.defaultPrevented) return
    klappeUm()
  }
  // 🔴 G5b/P35: `gesteFuer` und `useSwipeRow` liefern BEIDE `onPointerDown` &
  // Co. Bis zur Nachbesserung ueberschrieb der spaetere Swipe-Spread den
  // Langdruck — der Auswahlmodus startete nie (P35 mobil rot). Die Handler
  // werden deshalb zusammengesetzt; die Stile ebenso (der Swipe traegt
  // `touch-action: pan-y`, die Geste die iOS-Auswahl-Eigenschaften).
  const zeigerHandlers = {
    ...(geste || {}),
    ...swipeHandlers,
    style: { ...(geste?.style || {}), ...(swipeHandlers.style || {}) },
    onPointerDown: (e) => { geste?.onPointerDown?.(e); swipeHandlers.onPointerDown?.(e) },
    onPointerMove: (e) => { geste?.onPointerMove?.(e); swipeHandlers.onPointerMove?.(e) },
    onPointerUp: (e) => { geste?.onPointerUp?.(e); swipeHandlers.onPointerUp?.(e) },
    onPointerCancel: (e) => { geste?.onPointerCancel?.(e); swipeHandlers.onPointerCancel?.(e) },
    onPointerLeave: (e) => { geste?.onPointerLeave?.(e); swipeHandlers.onPointerLeave?.(e) },
  }

  const kartenAktionen = [
    // 🔴 R16 N3: auf einer gewählten Karte hebt der Eintrag die Wahl auf
    // („Deselect"), statt idempotent „Select" zu heissen (§11.6).
    auswahl?.isSelected(email.id)
      ? { key: 'deselect', label: 'Deselect', onClick: () => auswahl.toggle(email.id) }
      : { key: 'select', label: 'Select', onClick: () => auswahl?.startSelection(email.id) },
    {
      key: 'link', label: etikett.link,
      icon: <LinkIcon fontSize="small" />,
      onClick: () => setLinkOpen(true), disabled: isProcessing,
    },
    {
      key: 'reprocess', label: 'Reprocess', icon: <ReplayIcon fontSize="small" />,
      onClick: () => onReprocess(email.id),
      hidden: !email.po_id, disabled: isProcessing,
    },
    {
      key: 'ignore', label: etikett.ignore, icon: <BlockIcon fontSize="small" />,
      divider: true, onClick: () => onIgnore(email.id),
      hidden: email.action_taken === 'ignored', disabled: isProcessing,
    },
  ]

  return (
    // G10 — der Vordergrund ist DECKEND (`background.paper`), die Kachel liegt
    // HINTER ihm. Ein durchscheinender Ton liesse sie durch (Fotobefund G5-2).
    <Box sx={{ position: 'relative', overflow: 'hidden', borderRadius: PANEL_RADIUS }}>
      {Math.abs(dx) > WISCH_FELD_SICHTBAR_AB && (
        <Stack direction="row" alignItems="center" spacing={SP.chip}
          justifyContent={wischLinks ? 'flex-end' : 'flex-start'}
          data-testid="po-inbox-wisch-feld"
          sx={{
            position: 'absolute', inset: 0, px: SP.tight,
            ...wischFlaeche(wischLinks ? 'warn' : 'info'),
            fontSize: FS.sm, fontWeight: 600,
          }}>
          {wischLinks ? <BlockIcon /> : <LinkIcon />}
          <span>{wischLinks ? etikett.ignore : etikett.link}</span>
        </Stack>
      )}
      {/* 🔴 M3-Nachbesserung B3 — die Kachel sitzt im RUHENDEN Behaelter, an
          DESSEN Rand, und der Vordergrund gleitet darueber weg. Sie darf kein
          Nachfahre der Karte sein: die traegt `transform: translateX(…)`, und
          ein Kind darin wanderte mit. */}
      {aufgedeckt && (
        <Button
          data-testid="po-inbox-wisch-kachel"
          onClick={() => {
            const seite = aufgedeckt
            setAufgedeckt(null)
            if (seite === 'left') onIgnore(email.id)
            else setLinkOpen(true)
          }}
          startIcon={aufgedeckt === 'left' ? <BlockIcon /> : <LinkIcon />}
          sx={{
            position: 'absolute', top: 0, bottom: 0,
            [aufgedeckt === 'left' ? 'right' : 'left']: 0,
            width: PO_WISCH_KACHEL_W, minWidth: PO_WISCH_KACHEL_W,
            px: SP.chip,
            ...wischFlaeche(aufgedeckt === 'left' ? 'warn' : 'info'),
            fontSize: FS.sm, fontWeight: 600,
            // 🔴 R16 B1 — `MuiButton` setzt hausweit `whiteSpace: 'nowrap'`;
            // ohne diese Zeile laeuft das Etikett aus der 112-px-Kachel heraus.
            whiteSpace: 'normal', textAlign: 'center',
            ...hoverOnly({ bgcolor: wischFlaeche(aufgedeckt === 'left' ? 'warn' : 'info').bgcolor }),
          }}>
          {aufgedeckt === 'left' ? etikett.ignore : etikett.link}
        </Button>
      )}

      {/* 🔴 G4b §2.6/A4 — die handgerollte Karte ist durch den geteilten
          Baustein `MobileRecordCard` ersetzt („keine Kartenkomponente je
          Ansicht", A4). `slotOrder="chips-first"` folgt N11.2 (Titel → Kennung →
          Status → Kerndaten); `expanded` traegt den aufgeklappten Teil.
          🔴 `fieldTestIds` sind die Pruefflaeche der §15-Proben:
          `karte-kennung-*` (P20/P35), `karte-status-*` (P22 mobil). */}
      <MobileRecordCard
        // 🔴 KEIN `role="button"`/`tabIndex` am Wurzelknoten (Orchestrator-
        // Entscheid A, 2026-10-18): `role="button"` macht seine Nachfahren
        // praesentational — Checkbox, Primaerknopf und ⋮ verschwaenden aus dem
        // A11y-Baum (R16 B1). Das Bedienelement ist der TITEL-Knopf unten; die
        // Flaeche traegt nur den Zeiger-Komfort.
        data-testid={`karte-${email.id}`}
        slotOrder="chips-first"
        fieldTestIds={{
          title: `karte-titel-${email.id}`,
          id: `karte-kennung-${email.id}`,
          chips: `karte-status-${email.id}`,
          meta: `karte-meta-${email.id}`,
          actions: `karte-aktionen-${email.id}`,
        }}
        title={(
          // 🔴 Der TITEL ist das Bedienelement (Entscheid A): ein echter Knopf,
          // `aria-expanded` sagt den Aufklappzustand an. Die Kartenfläche
          // daneben bleibt Zeiger-Komfort — nicht im A11y-Baum.
          <Box
            component="button"
            type="button"
            data-testid={`karte-titel-knopf-${email.id}`}
            aria-expanded={expanded}
            onClick={handleTitelKlick}
            sx={{
              display: 'block', width: '100%', minWidth: 0, p: 0, m: 0,
              border: 0, bgcolor: 'transparent', color: 'inherit',
              font: 'inherit', textAlign: 'left', cursor: 'pointer',
            }}
          >
            <Typography variant="body2" fontWeight={isSeen ? 400 : 700} noWrap sx={{ minWidth: 0 }}>
              {email.from_name || email.from_email}
            </Typography>
          </Box>
        )}
        // 🔴 Die Kennung wird NIE gekuerzt (N11.5, `MobileRecordCard`).
        id={kennung}
        chips={(
          <Stack direction="row" spacing={SP.chip} alignItems="center" sx={{ minWidth: 0 }}>
            {/* 🔴 Entscheid B (2026-10-18): der Intent-Ring steht in der
                CHIP-Zeile, vor dem Intent-Chip — dieselbe Folge wie die
                Desktop-Zeile. Er kostete als eigene Zeile unter der
                Zusammenfassung 20 px (44-px-Knopfzeile vs. 24-px-Ring). */}
            <Box sx={{ display: 'flex', flexShrink: 0 }}>
              <ConfidenceBadge confidence={email.llm_confidence} intent={email.llm_intent} />
            </Box>
            {email.llm_intent && (
              <Chip size="small" label={intent.label}
                title={intent.unmapped ? UNMAPPED_LABEL_HINT : undefined}
                color={INTENT_COLOR[email.llm_intent] || 'default'}
                sx={{ height: 20, fontSize: FS.xs, minWidth: 0, flexShrink: 1 }} />
            )}
            <Chip size="small" variant="outlined"
              label={action.label}
              title={action.unmapped ? UNMAPPED_LABEL_HINT : undefined}
              color={ACTION_COLOR[email.action_taken] || 'default'}
              sx={{
                height: 20, fontSize: FS.xs, fontStyle: action.unmapped ? 'italic' : undefined,
                minWidth: 0, flexShrink: 1,
              }} />
          </Stack>
        )}
        meta={(
          <Box>
            {/* Kerndaten: Betreff/Zusammenfassung und die Zeit. */}
            <Stack direction="row" alignItems="baseline" spacing={SP.chip}>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0, flex: 1 }}>
                {email.llm_summary || email.subject || '(no subject)'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                {formatRelative(email.received_at)}
              </Typography>
            </Stack>
            {/* Die „Warum"-Zeile — NUR wenn es einen Triage-Grund gibt. Ohne
                ihn entsteht keine Zeile (und kein `mt`): das war der zweite
                20-px-Posten der Karte. Der Hauptknopf steht seit der
                Nachbesserung in der Titelzeile (`actions`), nicht hier. */}
            {triage && (
              <Typography variant="caption" noWrap title={triage.detail}
                sx={{ display: 'block', mt: SP.chip, color: triageFarbe }}>
                {triage.text}
              </Typography>
            )}
          </Box>
        )}
        actions={(
          // 🔴 Entscheid B: der EINE Hauptknopf steht rechts im KOPF, neben dem
          // ⋮. Die Titelzeile ist wegen des ⋮ ohnehin 44 px hoch — der Knopf
          // kostet dort 0 px, waehrend er als eigene Zeile 44 px trug. Der
          // ⋮-Traeger behaelt seinen `onPointerDown`-Stopper (N2-Falle,
          // `pattern-library.md:478`).
          <Stack direction="row" spacing={SP.chip} alignItems="center" sx={{ flexShrink: 0 }}>
            {primary && (
              <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'flex', flexShrink: 0 }}>
                {primary === 'link' && (
                  <Button size="small" variant="text" startIcon={<LinkIcon />}
                    onClick={() => setLinkOpen(true)}>
                    Link to PO
                  </Button>
                )}
                {primary === 'reprocess' && (
                  <Button size="small" variant="text" startIcon={<ReplayIcon />}
                    onClick={() => onReprocess(email.id)}
                    disabled={isProcessing}>
                    Reprocess
                  </Button>
                )}
              </Box>
            )}
            <Box onPointerDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
              <RowActionsMenu
                label={`Row actions for ${kennung}`}
                actions={kartenAktionen}
              />
            </Box>
          </Stack>
        )}
        // 🔴 §11.6/N11.21: die Checkbox steht NUR im Auswahlmodus — „im
        // Ruhezustand bleibt die Karte unveraendert". `auswahl.active` ist
        // `count > 0`; P35 (a) zaehlt sie im Ruhezustand: 0.
        selectable={auswahl?.active ? {
          label: `Select ${kennung}`,
          checked: auswahl.isSelected(email.id),
          onChange: () => auswahl.toggle(email.id),
        } : undefined}
        // 🔴 Der Langdruck ist der ZWEITE Eintritt in den Modus (§11.6), an
        // `(pointer: coarse)` gebunden. `zeigerHandlers` setzt Geste UND Wisch
        // zusammen — ein blosses Nacheinander-Spreaden ueberschrieb den
        // `onPointerDown` der Geste mit dem des Wischs, und der Langdruck
        // startete nie (P35 mobil rot).
        {...zeigerHandlers}
        // 🔴 G9: der aufgeklappte Teil wird NUR uebergeben, wenn er offen ist —
        // `MobileRecordCard` rendert `expanded` unbedingt, und ein leerer
        // Kasten mit `mt` waere die stille Variante. Im geschlossenen Zustand
        // gibt es damit keinen fokussierbaren und keinen angesagten Inhalt
        // (G4-V-Uebergabe, R16 C1).
        expanded={expanded ? (
          <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: SP.tight }}>
            {triage?.detail && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: SP.chip }}>
                {triage.detail}
              </Typography>
            )}
            {email.body_text && (
              <Box sx={EMAIL_BODY_SX}>
                {email.body_text}
              </Box>
            )}
            {(extracted.confirmed_delivery_date || extracted.delay_days) && (
              <Stack spacing={0.25} sx={{ mt: SP.tight }}>
                {extracted.confirmed_delivery_date && (
                  <Typography variant="caption" color="text.secondary">
                    Delivery date: <strong>{extracted.confirmed_delivery_date}</strong>
                  </Typography>
                )}
                {extracted.delay_days && (
                  <Typography variant="caption" color="text.secondary">
                    Delay: {extracted.delay_days} days
                  </Typography>
                )}
              </Stack>
            )}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mt={SP.tight}>
              {email.po_id && (
                <Button size="small" variant="outlined"
                  onClick={() => navigate(`/purchase_order/${email.po_id}`)}>
                  Open PO
                </Button>
              )}
              <Button size="small" variant="outlined" startIcon={<LinkIcon />}
                onClick={() => setLinkOpen(true)} disabled={isProcessing}>
                {email.po_id ? 'Reassign PO' : 'Link to PO'}
              </Button>
              {email.po_id && (
                <Button size="small" startIcon={<ReplayIcon />}
                  onClick={() => onReprocess(email.id)} disabled={isProcessing}>
                  Reprocess
                </Button>
              )}
              {email.action_taken !== 'ignored' && (
                <Button size="small" startIcon={<BlockIcon />}
                  onClick={() => onIgnore(email.id)} disabled={isProcessing}>
                  Ignore &amp; archive
                </Button>
              )}
            </Stack>
          </Box>
        ) : null}
        sx={{
          position: 'relative',
          // 🔴 Der DECKENDE Vordergrund (zIndex 2, Hausform
          // `RMACasesDashboardBody.jsx`).
          zIndex: 2,
          cursor: 'pointer',
          // G9: die Deckkraft ist der GELESEN-Zustand (K.3 misst sie).
          opacity: isSeen && !expanded ? 0.80 : 1,
          borderLeft: `${RAIL_WIDTH}px solid`,
          borderLeftColor: INTENT_COLOR[email.llm_intent]
            ? `${INTENT_COLOR[email.llm_intent]}.main` : 'divider',
          transform: `translateX(${wischVersatz}px)`,
          transition: swiping ? 'none' : 'transform 0.18s ease',
          // G8 — `MotionConfig` (`App.js:213`) deckt nur framer-motion ab, nicht
          // diese CSS-Transition; die Abschaltung steht in `inbox/wischRuecknahme.js`.
          ...WISCH_BEWEGUNG_SX,
          ...hoverOnly({ bgcolor: 'action.hover' }),
        }}
        onPointerDownCapture={() => { wischGewischt.current = false }}
        // Zuletzt: der zusammengesetzte Klickweg gewinnt gegen das `onClick`
        // aus `gesteFuer` (das der Spread oben mitbringt).
        onClick={handleKarteKlick}
      />
      {/* Link-to-PO dialog — the Autocomplete needs the width a phone does not have
          inside a card, same shape as the RMA card's link-to-case dialog. */}
      <Dialog open={linkOpen} onClose={() => setLinkOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{email.po_id ? 'Reassign to PO' : 'Link to purchase order'}</DialogTitle>
        <DialogContent dividers>
          <PoLinkPicker
            email={email}
            link={link} setLink={setLink}
            poOptions={poOptions} poLoading={poLoading}
            poError={poError} onRetryOptions={onRetryOptions}
            onLinked={onLinked}
            onCancel={() => setLinkOpen(false)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
// ── Main component ─────────────────────────────────────────────────────────────

/** Das Suchfeld der Reihe — der FELD-Boden an BEIDEN Stellen des Bausteins.
 *
 *  🔴 T3-1 (UI-Durchgang 2 Teil 3, 2026-10-08): „eine Hoehe je Suchreihe" ist
 *  nur erreichbar, wenn das Feld denselben Boden traegt wie die Knoepfe daneben.
 *  `size="small"` ist auf dieser Flaeche **37,1 px** und nicht 40: MUI rechnet
 *  die `small`-Hoehe aus `1,4375em` Zeilenhoehe plus zweimal 8,5 px Innenabstand,
 *  und das Haus-Theme setzt `htmlFontSize: 14` (`theme.js:310`) —
 *  `1,4375 × 14 + 17 = 37,125`. Hausform und Begruendung: `tokens.js:710-764`.
 *
 *  Die zwei Teile sind beide noetig: `...FIELD_HEIGHT_SX` an der WURZEL (der
 *  `TextField`-Wurzel) und der Nachfahre `& .MuiInputBase-root` fuer den
 *  SICHTBAREN Kasten — ohne den zweiten Teil waere die Wurzel 40 px hoch und
 *  der Umriss weiter 37,1 px. Gilt fuer den DESKTOP; auf dem Telefon greift
 *  `SUCHFELD_MOBIL_SX` darunter.
 *
 *  ⚠️ Damit traegt die Inbox das Feld auf dem Haus-Boden, waehrend andere
 *  `SearchTrigger`-Aufrufer noch bei 37,1 px stehen. Das ist die Absicht (der
 *  Token IST der Boden) — die flaechendeckende Umstellung gehoert zu PR C
 *  (F5, `SearchTrigger.jsx` selbst). Hier steht sie als `sx` des Aufrufers,
 *  weil die Datei jenem PR gehoert. */
const SUCHFELD_SX = {
  flex: 1,
  ...FIELD_HEIGHT_SX,
  '& .MuiInputBase-root': { ...FIELD_HEIGHT_SX, alignItems: 'center' },
}

/** Dieselbe Hoehe auf dem TELEFON — ohne den Eingriff in das Eingabefeld.
 *
 *  🔴 `SearchTrigger` hat auf dem Telefon ZWEI Kaesten: der aeussere nimmt das
 *  `sx` des Aufrufers, der innere ist die sichtbare Pille und traegt ein festes
 *  `height: 40` (`SearchTrigger.jsx:183`). Der Nachfahre aus `SUCHFELD_SX`
 *  traefe den `InputBase` INNERHALB der Pille und zoege ihn auf 44 px — in
 *  einer 40-px-Pille ragte er oben und unten heraus. Auf dem Telefon traegt
 *  deshalb nur der aeussere Kasten den Boden; das Eingabefeld bleibt, wie es
 *  ist. 🔴 Die sichtbare Pille selbst kommt mit PR C auf 44 (F5, T3-5: „Suchpille
 *  40 px neben Filters 44 px") — die Datei `SearchTrigger.jsx` gehoert jenem PR.
 *  Bis dahin ist der PLATZ der Zeile einheitlich, die Pille selbst noch 4 px
 *  kleiner als ihre beiden Nachbarn. */
const SUCHFELD_MOBIL_SX = { flex: 1, ...FIELD_HEIGHT_SX }

/** 🔴 G4b (§2.8): der Symbolknopf der alten Suchreihe ist entfallen — mit dem
 *  `RefreshButton` (genau EIN Auffrisch-Weg: „Check inbox" im Kopf) und dem
 *  ⋮, das in `ListToolbar.actions` aufgegangen ist. Der Block
 *  `SUCHREIHE_KOPF_SX` stand hier bis G4b; ein ungenutzter Token waere ein
 *  ESLint-Befund (Regel 9), und „auf Vorrat behalten" ist keine Hausform. */

export default function POInbox() {
  const theme   = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [emails, setEmails]           = useState([])
  const [total, setTotal]             = useState(0)
  const [loadError, setLoadError]     = useState(false)
  const [loading, setLoading]         = useState(true)
  const [processingId, setProc]       = useState(null)
  const [checking, setChecking]       = useState(false)
  const [checkResult, setCheckResult] = useState(null)
  // 🔴 §2.5: die Bestaetigung des Sammel-Loeschens. `BulkActionBar.actions` sind
  // reine Knoepfe — der Bestaetigungsschritt des alten `InlineConfirmButton`
  // bleibt als Dialog (die Anforderung bleibt, nur die Form wechselt).
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false)
  // 🔴 `BulkActionBar.returnFocusRef` (R16 W3): beim Ausblenden der Leiste darf
  // der Fokus nicht auf `<body>` fallen — er geht auf den Listenkasten zurueck.
  const listenRef = useRef(null)
  // 🔴 Die Reiterzaehler (§2.3). Die Deklaration steht HIER, nicht beim Abrufer
  // weiter unten: `bestandM` liest sie beim Rendern (TDZ-/ESLint-Grund wie in
  // `RMAInbox.jsx`).
  const [counts, setCounts] = useState({})

  // 🔴 G4b (§3): die Ansicht liest ihren Zustand aus der ADRESSE — `tab`
  // (stabiler Schluessel, nie ein Index), `q` (Rohtext), `page` (ab 1).
  // `useListQuery` ist der EINZIGE Schreiber der drei (§3.3); die Ansicht
  // schreibt keinen zusaetzlich. Grundzustand ist `all`, NICHT `unread`, und
  // `all` steht VORN (ENTSCHEID F3/B3).
  // 🔴 Der PO-Deep-Link `?inbound=` bleibt ein FREMDER Parameter (§3.2, Frage 3):
  // PO hat heute keinen Leser, G4b baut keinen (kein neuer Zustand).
  const query = useListQuery({
    tabs: { default: 'all', values: TAB_KEYS },
    dimensions: [{ key: 'q', kind: 'single', label: 'Search' }],
    pageSize: INBOX_SEITENGROESSE,
  })
  const { state, set } = query
  const tab = TAB_KEYS.includes(state.tab) ? state.tab : 'all'
  const searchQuery = state.q ?? ''
  // 🔴 1-basiert (`useListQuery.js:489` — fehlt = 1); der PO-Abruf ist
  // `offset`-basiert und 0-basiert. Die zwei Umrechnungen (§3.1.1): Abruf
  // (`offset`) und Fusszeile (`page`).
  const page = state.page

  const [poOptions, setPoOptions] = useState([])
  const [poLoading, setPoLoading] = useState(false)
  const [poError, setPoError]     = useState(false)

  // Seen tracking — persisted to localStorage
  const [seenIds, setSeenIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('po_inbox_seen') || '[]')) }
    catch { return new Set() }
  })
  const markSeen = useCallback((id) => {
    if (seenIds.has(id)) return
    const next = new Set([...seenIds, id])
    setSeenIds(next)
    localStorage.setItem('po_inbox_seen', JSON.stringify([...next].slice(-500)))
  }, [seenIds])
  const markAllSeen = useCallback(() => {
    const next = new Set([...seenIds, ...emails.map(e => e.id)])
    setSeenIds(next)
    localStorage.setItem('po_inbox_seen', JSON.stringify([...next].slice(-500)))
  }, [seenIds, emails])

  // Unread snapshot — stable per tab/page/search so clicking doesn't
  // immediately push items out of the Unread view
  const unreadSnapshot = useRef(new Set())
  useEffect(() => {
    unreadSnapshot.current = new Set(seenIds)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, page, searchQuery])

  const displayedEmails = (() => {
    if (tabFuer(tab)?.clientFilter === 'unread')
      return emails.filter(e => !unreadSnapshot.current.has(e.id))
    return emails
  })()

  // 🔴 G4b (§2.3.1): der Zaehler des `unread`-Reiters — „ungelesen innerhalb der
  // GELADENEN SEITE". Der Browser hat nur die geladene Seite und das GEKUEPPTE
  // Gelesen-Set; ueber die volle Menge ist „ungelesen" daraus nicht zaehlbar.
  const unreadCount = emails.filter(e => !unreadSnapshot.current.has(e.id)).length

  // 🔴 §4: N und M des Trefferzaehlers (Z1). N = `total` der AKTUELLEN Abfrage
  // (mit Suche), M = der Grundbestand des Reiters OHNE Suche (= Reiterzaehler).
  // 🔴 Ausnahme `unread`: dort ist der Server-`total` die `all`-Menge, weil die
  // Filterung clientseitig laeuft — N und M kommen fuer diesen EINEN Reiter aus
  // der gefilterten Client-Menge (§2.3.1), nie aus dem Server-`total`.
  const istUnread = tabFuer(tab)?.clientFilter === 'unread'
  const trefferN = istUnread ? displayedEmails.length : total
  const bestandM = istUnread ? unreadCount : (counts[tab] ?? null)

  // 🔴 §2.8/N8.5: die Sperre gilt NUR beim ersten Aufbau — „Fehler UND noch nie
  // geladen". Ein Auffrisch-Fehler sperrt NICHT (die alten Zeilen stehen und
  // tragen ihre eigene Meldung). `jeGeladen` wird im ERFOLGSZWEIG gesetzt, nie
  // in `.finally` (§2.7).
  const [jeGeladen, setJeGeladen] = useState(false)
  const ersteAufbauGesperrt = loadError && !jeGeladen
  // 🔴 §2.7 (R16 W4): ein gescheiterter ZAehl-Abruf laesst die Ziffer weg —
  // richtig (Regel 22 Nr. 3), aber bis zur Nachbesserung ohne sichtbaren Grund.
  // Die Reiterreihe sagt jetzt, dass die Ziffern fehlen. Erst NACH `jeGeladen`,
  // sonst stuende der Satz schon im Aufbau ueber einer noch leeren Leiste.
  const zaehlerFehlen = jeGeladen && Object.values(counts).some(v => v === null)

  // 🔴 Bulk-Auswahl (G4b §2.5, BS16): `useRowSelection` ersetzt den Eigenbau.
  // Es LEERT die Auswahl selbst, sobald sich die Abfrage aendert (Reiter, Suche
  // — §9.5/N10.3); die Schwelle haengt am `mode` der Leiste (`inline` 2,
  // `mobile` 1) statt an einer festen 2 in beiden Zweigen.
  // 🔴 `total` wird ABSICHTLICH NICHT uebergeben: Stufe 2 („Select all M
  // matching") ist in G4 nicht gebaut (§2.5, Orchestrator-Entscheid Frage 2).
  // Uebergeben waere `canSelectAllMatching` wahr, der Knopf erschiene, und
  // `count` zaehlte `total − excluded.size`, waehrend die Handler die GELADENE
  // Seite senden — ein angegebener Umfang, der nicht der ausgefuehrte ist.
  const auswahl = useRowSelection({
    query,
    pageKeys: displayedEmails.map(e => e.id),
    headerLabel: 'Select all rows on this page',
  })
  // Die gewaehlten Kennungen fuer die Sammel-Handler — der Schnitt mit der
  // geladenen Seite, ehrlich hergeleitet (Stufe 2 ist abgeschaltet).
  const gewaehlteIds = useCallback(
    () => displayedEmails.filter(e => auswahl.isSelected(e.id)).map(e => e.id),
    [displayedEmails, auswahl],
  )
  // 🔴 Die SEITE steht nicht in der Signatur des Hooks (`useRowSelection.js:182`
  // schliesst `page`/`pageSize` aus). Ein Seitenwechsel leert die Auswahl
  // deshalb hier — sonst nennt die Leiste die Zahl der vorigen Seite, waehrend
  // die Handler die neue (leere) senden.
  const auswahlLeeren = auswahl.clear
  const seiteZuvor = useRef(page)
  useEffect(() => {
    if (seiteZuvor.current === page) return
    seiteZuvor.current = page
    auswahlLeeren()
  }, [auswahlLeeren, page])

  // 🔴 G4b (§2.8): die Entprellung sitzt im `SearchTrigger`-PUFFERMODUS
  // (`onCommit`, 300 ms), den `ListToolbar` durchreicht — ein handgebauter
  // Zeitgeber daneben waere die zweite Quelle derselben Sache (N7.7).
  const clearSearch = useCallback(() => set('q', null), [set])

  // Fetch
  // 🔴 `hasLoadedOnce` ist ein REF, kein State: `fetchEmails` haengt damit nicht
  // an `jeGeladen`, und der Effekt laeuft nicht ein zweites Mal, sobald der
  // Erfolgszweig `jeGeladen` setzt (Hausform `RMAInbox.jsx`).
  const hasLoadedOnce = useRef(false)
  const fetchEmails = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent && !hasLoadedOnce.current) setLoading(true)
      // 🔴 §3.1.1: `page` ist 1-basiert (Hook), `offset` 0-basiert (Route).
      const params = { limit: INBOX_SEITENGROESSE, offset: (page - 1) * INBOX_SEITENGROESSE }
      // 🔴 `filter` IMMER senden (B4/R16 B3): ein FEHLENDES `filter` liefert
      // `unmatched`, nicht „all" (`routes.py:3452`). `''` erreicht den
      // else-Zweig der Route und damit die ganze, nicht archivierte Menge.
      // 🔴 UND bei gesetzter Suche genauso: die Suche ist ein Schnitt INNERHALB
      // des Reiters (§3.1/§4). Bis zur Nachbesserung fiel `filter` bei `q` ganz
      // weg — die Suche im Reiter „All" durchsuchte dann nur `unmatched`.
      params.filter = tabFuer(tab)?.filter ?? ''
      if (searchQuery) params.q = searchQuery
      const res = await axios.get(`${API}/api/purchase_orders/inbox`, { params })
      setEmails(res.data.items || [])
      setTotal(res.data.total || 0)
      setLoadError(false)
      // 🔴 §2.7: `jeGeladen` im ERFOLGSZWEIG, NIE in `.finally` — ein
      // fehlgeschlagener Abruf darf die Sperre des ersten Aufbaus nicht loesen.
      hasLoadedOnce.current = true
      setJeGeladen(true)
    } catch (e) {
      console.error('[PO inbox]', e)
      // 🔴 Ein fehlgeschlagener Abruf liess `emails` leer — und die Flaeche
      // zeigte daraufhin die Inbox-Zero-Feier. Der Zustand muss den Grund
      // kennen, sonst behauptet die Anzeige Ruhe, wo ein Fehler war.
      setLoadError(true)
    }
    finally { if (!silent) setLoading(false) }
  }, [tab, page, searchQuery])

  // Silent on auto-fetches (tab/page/search change) — no flicker on switch.
  useEffect(() => { fetchEmails({ silent: hasLoadedOnce.current }) }, [fetchEmails])

  // 🔴 G4b (§2.3): die Reiterzaehler — vier Kleinabrufe (`limit=1`), EINMAL je
  // Aufbau und beim Auffrischen, NICHT je Reiterwechsel (Grundbestand ohne
  // Suche). Ein FEHLGESCHLAGENER Abruf liefert `null`, NIE `0` (Regel 22 Nr. 3,
  // §2.7); eine GEMESSENE 0 (PO `auto-handled` prod 0) ist eine Zahl und steht.
  const fetchCounts = useCallback(async () => {
    const paare = await Promise.all(ZAEHL_ABRUFE.map(async (s) => {
      try {
        const res = await axios.get(`${API}/api/purchase_orders/inbox`, { params: s.params })
        // 🔴 Regel 22 Nr. 3: eine FEHLENDE Zahl ist unbekannt, nicht 0. Nur
        // eine wirklich gelieferte Zahl ist eine Messung; sonst fiele der
        // Reiter still auf „0" und behauptete Ruhe.
        return [s.key, typeof res.data?.total === 'number' ? res.data.total : null]
      } catch { return [s.key, null] }
    }))
    setCounts(Object.fromEntries(paare))
  }, [])
  useEffect(() => { fetchCounts() }, [fetchCounts])

  // Load PO options for the link widget. §5.3: a failed fetch used to be
  // swallowed by `.catch(() => {})`, so the widget opened with an empty dropdown
  // that was indistinguishable from "no PO matches".
  const loadPoOptions = useCallback(() => {
    setPoLoading(true)
    setPoError(false)
    return axios.get(`${API}/api/purchase_orders`, { params: { limit: 200 } })
      .then(r => {
        setPoOptions(
          (r.data.purchase_orders || r.data || [])
            .filter(p => ['Order placed', 'Paid', 'In Delivery'].includes(p.status))
            .map(p => ({
              po_id: p.po_id,
              label: `${p.po_id} — ${p.vendor_name || p.vendor_id || ''}`,
            }))
        )
      })
      .catch(e => { console.error('[PO inbox] PO list failed', e); setPoError(true) })
      .finally(() => setPoLoading(false))
  }, [])

  useEffect(() => { loadPoOptions() }, [loadPoOptions])

  // Check inbox manually — the ONE refresh path (§2.8, N2.6).
  const handleCheckInbox = async () => {
    setChecking(true); setCheckResult(null)
    try {
      const res = await axios.post(`${API}/api/purchase_orders/process-inbox`)
      setCheckResult(res.data)
      // 🔴 R16 W2: der Abruf holt Seite 1 — dann muss die ADRESSE auch Seite 1
      // sagen. Vorher lud der Knopf `offset: 0`, waehrend `?page=3` stehen
      // blieb: Fusszeile und Adresse behaupteten Seite 3, die Liste zeigte 1.
      if (page !== 1) set('page', 1)
      const fresh = await axios.get(`${API}/api/purchase_orders/inbox`, {
        params: {
          limit: INBOX_SEITENGROESSE,
          offset: 0,
          // 🔴 Dieselbe Regel wie im regulaeren Abruf (B4/R16 B3): `filter`
          // immer, `q` zusaetzlich.
          filter: tabFuer(tab)?.filter ?? '',
          ...(searchQuery ? { q: searchQuery } : {}),
        },
      })
      const freshItems = fresh.data.items || []
      setTotal(fresh.data.total || 0)
      // 🔴 Seite 1 IST die ganze Ansicht nach dem Zuruecksetzen — kein
      // Zusammenfuehren mit den Zeilen der alten Seite (die gehoerten zu einer
      // Seite, die die Adresse nicht mehr nennt).
      setEmails(freshItems)
      fetchCounts()
    } catch (e) {
      setCheckResult({ error: e.response?.data?.error || 'Failed' })
    } finally { setChecking(false) }
  }

  // Optimistic mutations
  const applyPatch = (id, patch) =>
    setEmails(prev => prev.map(e => e.id === id ? { ...e, ...patch } : e))

  const handleIgnore = async (id) => {
    setProc(id)
    try {
      await axios.post(`${API}/api/purchase_orders/inbox/${id}/ignore`)
      applyPatch(id, { action_taken: 'ignored', is_archived: true })
      if (!tabFuer(tab)?.archived) {
        // 🔴 Die Zeile verlaesst die geladene Seite — ihr Schluessel verlaesst
        // mit ihr die Auswahl. `auswahl.count` zaehlt `keys.size`, die Handler
        // schneiden gegen `displayedEmails`; bliebe der Schluessel stehen,
        // nannte die Leiste mehr, als ausgefuehrt wuerde (dieselbe Klasse wie
        // G4a-NB7). `isSelected` + `toggle` ist die API fuer genau EINE Kennung.
        if (auswahl.isSelected(id)) auswahl.toggle(id)
        setEmails(prev => prev.filter(e => e.id !== id))
      }
      fetchCounts()
    } catch { fetchEmails({ silent: true }) } finally { setProc(null) }
  }

  const handleReprocess = async (id) => {
    setProc(id)
    try {
      const res = await axios.post(`${API}/api/purchase_orders/inbox/${id}/reprocess`)
      applyPatch(id, res.data || {})
      fetchCounts()
    } catch { fetchEmails({ silent: true }) } finally { setProc(null) }
  }

  const handleLinked = (id, poId) => {
    applyPatch(id, { po_id: poId, action_taken: 'matched' })
    fetchCounts()
  }

  // 🔴 G4b-Nachbesserung 3 (R16 W4): die GANZE Kopfzelle waehlt aus/ab, nicht
  // nur das Kaestchen. Ein Klick auf das Kaestchen selbst laeuft ueber
  // seinen eigenen `onChange`. Ein zweiter Aufruf hier waere fuer den KOPF
  // idempotent (`selectPage`/`clearPage` lesen denselben Stand) — der
  // Ausschluss ist hier defensiv, nicht beobachtbar (Mutation C, NB3).
  // MUI legt sein Eingabefeld deckend ueber das ganze Kaestchen, deshalb
  // trifft jeder Klick darauf das `<input>`.
  const handleKopfAuswahl = (e) => {
    e.stopPropagation()
    if (e.target.closest('input[type="checkbox"]')) return
    auswahl.headerCheckbox.onChange()
  }

  // Bulk actions — die Handler lesen `gewaehlteIds()` (die GELADENE Seite),
  // nicht `auswahl.count`; `total` ist bewusst nicht am Hook (s. o.).
  const handleBulkMarkRead = useCallback(() => {
    const next = new Set([...seenIds, ...gewaehlteIds()])
    setSeenIds(next)
    localStorage.setItem('po_inbox_seen', JSON.stringify([...next].slice(-500)))
    auswahl.clear()
  }, [seenIds, gewaehlteIds, auswahl])

  const handleBulkDelete = useCallback(async () => {
    const ids = gewaehlteIds()
    try {
      await axios.post(`${API}/api/purchase_orders/inbox/bulk-delete`, { ids })
      setEmails(prev => prev.filter(e => !ids.includes(e.id)))
      fetchCounts()
    } catch (e) {
      // 🔴 Vorher: `catch { fetchEmails({ silent: true }) }` — ein
      // fehlgeschlagenes Loeschen lud still nach und der Nutzer sah NICHTS.
      // Eine destruktive Handlung, die scheitert und schweigt, ist schlimmer
      // als eine, die scheitert und es sagt.
      console.error('[PO inbox] bulk delete failed', e)
      toast.error('Delete failed.')
      fetchEmails({ silent: true })
    }
    auswahl.clear()
  }, [gewaehlteIds, auswahl, fetchEmails, fetchCounts])

  // 🔴 §2.2: die ⋮-SEITENAKTION der Leiste (PO: „Mark read (this device)";
  // „Archive Done" gibt es in dieser Inbox nicht). `actionsLabel` ist Pflicht,
  // sobald `actions` steht — sonst wirft die Leiste (`ListToolbar.jsx:236-241`).
  const seitenAktionen = [
    {
      key: 'mark-read',
      label: 'Mark read (this device)',
      icon: <DoneAllIcon fontSize="small" />,
      onClick: markAllSeen,
    },
  ]

  // 🔴 §2.7: die vier Lagen des Leerzustands — EINE Form (`EmptyState`, BS13).
  // `reason` ist Pflicht, und JEDER Reiter traegt seine EIGENE Erwartung
  // (`leerzustandFuer` → `LEER_JE_REITER`, `all` eingeschlossen).
  // Die dritte Lage der Karte („kein Treffer — Filter") ist GEGENSTANDSLOS:
  // nichts engt ein ausser Suche und Reiter, und der Reiter ist kein Filter-Chip.
  const leerProps = (() => {
    if (loadError) {
      return {
        reason: 'error',
        message: 'Messages could not be loaded.',
        detail: 'This is a load failure, not an empty inbox.',
        onRetry: () => fetchEmails(),
      }
    }
    if (searchQuery) {
      // Das Begriff-Echo baut der Baustein selbst (`noun` = EINZAHL, §9 #15).
      return {
        reason: 'no-match',
        noun: 'message',
        query: searchQuery,
        // 🔴 R16 W1: nur Felder, die die Route WIRKLICH durchsucht
        // (`backend/purchase_orders/routes.py`: from_email, from_name, subject,
        // po_id). `llm_summary` ist NICHT dabei, `po_id` fehlte in der alten
        // Aufzaehlung.
        searchedIn: 'vendor, subject and PO number',
        onClearSearch: clearSearch,
      }
    }
    return leerzustandFuer(tab)
  })()

  return (
    <PageContainer sx={{ pb: 6 }} boxSx={{ display: 'flex', flexDirection: 'column', gap: SP.section }}>
      {/* 🔴 Zonen-Marke `liste-kopf` VON HAND (G4b §5.3): die Marken kommen
          NICHT mit den Bausteinen. Vorbilder sind `POOverview.jsx` (eine echte
          Ansicht, vier Marken von Hand) und `ListenMuster.jsx`. 🔴 `oeffne(page)`
          wartet auf `SEL.panel` — ohne `liste-panel` HAENGT jede ueber `oeffne`
          laufende Probe. */}
      <Box data-testid="liste-kopf">
        <ViewHeader
          title="Procurement Inbox"
          breadcrumbs={[
            { label: 'Purchase Orders', href: '/overview' },
            { label: 'Inbox' },
          ]}
          actions={[{
            key: 'check',
            label: checking ? 'Checking…' : 'Check inbox',
            icon: <SyncIcon />,
            loading: checking,
            onClick: handleCheckInbox,
          }]}
        />
      </Box>

      {/* Der Ergebnis-Hinweis des manuellen Laufs — er stand bis G4b in der
          Suchreihe; die ist Z1 geworden und traegt ihn nicht mehr. */}
      {checkResult && (
        <Typography variant="caption" color={checkResult.error ? 'error.main' : 'success.main'}>
          {checkResult.error ? checkResult.error : `✓ ${checkResult.processed ?? 0} new`}
        </Typography>
      )}

      {/* ── Zone 3 — Reiter (§5.4, G4b): UEBER dem Daten-Panel, JEDER Reiter mit
          Zaehler. Die Suche ist NICHT mehr hier — sie wandert in Z1 IM Panel. ── */}
      <Box data-testid="liste-reiter">
        <UnderlineTabs
          // 🔴 KEIN `scrollable` (A/B `g4b2`, V.5): die Prop schaltet das
          // Rollen/`nowrap` auch auf einem FEINEN Zeiger ein. Die Vorgabe des
          // Bausteins ist `scrollable ?? groberZeiger` — auf dem Telefon rollt
          // die Reihe (V.4), am Schreibtisch bricht sie um und die Reiter
          // schrumpfen (V.5). Fuenf Reiter passen bei 1440 px.
          ariaLabel="Inbox views"
          // 🔴 §5.4/P30: die Reiterform MIT Panel-Semantik (`withPanel` gibt
          // jedem Reiter eine `id`, dem gewaehlten ein `aria-controls`).
          withPanel
          panelIdPrefix={REITER_PANEL_ID}
          // 🔴 §2.3: `counts` ist die AUSGERICHTETE Form — die Leiste reserviert
          // die Breite der groessten Stellenzahl, die Reiter SPRINGEN nicht (P11).
          // Jeder SERVERSEITIGE Reiter traegt seinen GRUNDBESTAND (ohne Suche);
          // `unread` traegt „ungelesen innerhalb der geladenen Seite" (§2.3.1).
          // 🔴 `null` heisst „Abruf gescheitert" → KEINE Ziffer, nie eine 0
          // (§2.7, Regel 22 Nr. 3); eine GEMESSENE 0 steht (P11 verlangt eine
          // Ziffer). `unread` bleibt `null`, bis `jeGeladen` (sonst stuende im
          // Aufbau eine 0, obwohl die Zahl unbekannt ist).
          tabs={TABS.map(t => ({ label: t.label }))}
          counts={TABS.map(t => (t.clientFilter === 'unread' ? (jeGeladen ? unreadCount : null) : (counts[t.key] ?? null)))}
          // 🔴 `value` ist ein INDEX (der Baustein), `tab` ist der SCHLUESSEL
          // (§10.1) — die Abbildung steht hier, an EINER Stelle.
          value={TAB_INDEX[tab]}
          // 🔴 §5.6: der Reiterwechsel BEHAELT die Suche und LEERT die Auswahl
          // (`useRowSelection` tut das selbst beim Abfragewechsel, §9.5).
          // `page` setzt `useListQuery` auf 1 (N10.3).
          onChange={i => set('tab', TAB_KEYS[i])}
        />
        {/* 🔴 §2.3.0 (Grill W6): der Satz, der die Zaehler lesbar macht — `all`
            ist die OBERMENGE AUSSER Archived, die uebrigen sind Schnitte daraus.
            Dazu die GERAETE-Haelfte des `unread`-Zaehlers (§2.3.1). */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          All includes every other view except Archived. Unread shows mails you have not opened on this device, within the loaded page.
          {/* 🔴 R16 W4: der Grund, wenn die ZAehl-Abgrufe gescheitert sind. */}
          {zaehlerFehlen && ' Counts unavailable.'}
        </Typography>
      </Box>

      {/* 🔴 G4b (§2.2): das DATEN-PANEL traegt Z1 (Suche · Trefferzaehler ·
          ⋮ Seitenaktionen), Z3 (aktive Filter), die Liste UND die Fusszeile.
          Die Leiste stammt aus `ListToolbar` — sie stempelt `liste-z1`/`-z2`,
          Z3 kommt aus `ActiveFilterBar` INNERHALB der Leiste. */}
      <Panel
        pad="none"
        clip
        data-testid="liste-panel"
        // 🔴 P30: das Ziel von `aria-controls` muss `role="tabpanel"` tragen und
        // die `id` des GEWAEHLTEN Reiters fuehren. EIN Panel fuer alle Reiter —
        // die Reiter sind Sichten derselben Liste, nicht fuenf getrennte Panels.
        role="tabpanel"
        id={`${REITER_PANEL_ID}-panel-${TAB_INDEX[tab]}`}
        aria-labelledby={`${REITER_PANEL_ID}-tab-${TAB_INDEX[tab]}`}
      >
        <Box ref={listenRef} tabIndex={-1} sx={{ px: SP.card, pt: SP.card }}>
          {/* 🔴 §2.8/N8.5: der Fehler des ERSTEN Aufbaus sperrt die Suche und
              nennt den Grund SICHTBAR, direkt ueber der Leiste. Der Auffrisch-
              Fehler (Zeilen stehen schon) traegt seinen eigenen Satz UNTER der
              Leiste — zwei Aussagen, nicht zweimal derselbe Satz. */}
          {ersteAufbauGesperrt && (
            <Alert severity="warning" sx={{ mb: SP.card }} data-testid="erster-aufbau-gesperrt">
              Search is locked until the inbox loads. Use Try again below.
            </Alert>
          )}
          <ListToolbar
            shortcut="/"
            search={{
              value: searchQuery,
              // 🔴 PUFFERMODUS (`onCommit`): der Baustein haelt den Text und
              // meldet ihn EINMAL nach 300 ms Ruhe (N7.7: „ein Schreiber je
              // Abfrage"). `onChange` feuert dabei NICHT — es steht hier nur,
              // weil die Config beide Schluessel kennt (Hausform `RMAInbox`).
              onChange: v => set('q', v),
              onCommit: v => set('q', v),
              placeholder: 'Search vendor, subject, PO#…',
              label: 'Search vendor, subject, PO#',
              inputRole: 'searchbox',
              disabled: ersteAufbauGesperrt,
              // 🔴 T3-1: „eine Hoehe je Suchreihe" — das Feld traegt denselben
              // Boden wie die Knoepfe daneben (`FIELD_HEIGHT_SX`). `ListToolbar`
              // reicht `sx` ueber `{...search}` an `SearchTrigger` durch.
              sx: isMobile ? SUCHFELD_MOBIL_SX : SUCHFELD_SX,
            }}
            // 🔴 N4.5/P28: der Fehler hat KEINEN Trefferzaehler — der Leerzustand
            // spricht. 🔴 Regel 22 Nr. 3: `null`, solange `jeGeladen` falsch ist —
            // `trefferN`/`bestandM` sind vor der ersten Antwort 0, und der Zaehler
            // stuende im Aufbau als „0 messages" (die leere Region ist die
            // Hausform fuer „noch nicht gemessen").
            count={loadError ? undefined : { noun: 'messages', n: jeGeladen ? trefferN : null, m: jeGeladen ? bestandM : null }}
            // 🔴 §2.2: die ⋮-Seitenaktion (PO: „Mark read (this device)").
            // `actionsLabel` ist Pflicht, sobald `actions` steht.
            actions={seitenAktionen}
            actionsLabel="Inbox actions"
            query={{ chips: query.chips, clearAll: query.clearAll }}
          >
            {/* §5.3 — a failed REFETCH with rows already loaded keeps the rows
                and says so above them, instead of replacing loaded data with an
                empty state. */}
            {loadError && emails.length > 0 && (
              <Alert severity="error" sx={{ mt: SP.card }}
                action={<Button size="small" color="inherit" onClick={() => fetchEmails()}>Retry</Button>}>
                The inbox could not be reloaded. The rows below are the last set that loaded.
              </Alert>
            )}

            {/* 🔴 P13/N10.4: ein UNBEKANNTER Wert (`?tab=foo`) wird verworfen UND
                EINMAL gemeldet — stilles Ignorieren ist der Defekt, den die Probe
                misst. Dieselbe Hausform wie `/overview` und `/rma/inbox`: der
                Hinweis traegt die Marke `ignoriert-hinweis` und steht INNERHALB
                der Leiste (`liste-panel`). */}
            {query.ignoredValues.length > 0 && (
              <Alert severity="info" data-testid="ignoriert-hinweis" sx={{ mt: SP.card }}>
                {`Ignored unknown filter value “${query.ignoredValues[0].value}”.`}
              </Alert>
            )}

            {/* 🔴 §2.5 (BS16): EINE Sammelleiste je Liste, DAUERHAFT montiert —
                sie zeigt sich selbst erst ab der Schwelle ihres `mode` (`inline`
                2, `mobile` 1; `BulkActionBar.jsx:238`). Sie ERSETZT die zwei
                Bauformen des Eigenbaus (Kopf-Checkbox + eigene Leiste). */}
            <Box sx={{ mt: SP.card }}>
              <BulkActionBar
                selection={auswahl}
                label="Bulk actions"
                scope={tabFuer(tab)?.label}
                actions={[
                  { label: 'Mark read', icon: <DoneAllIcon />, onClick: handleBulkMarkRead },
                  // 🔴 §2.5: „Delete braucht `InlineConfirmButton`/`HoldButton`" —
                  // die Anforderung bleibt. `BulkActionBar.actions` sind reine
                  // Knoepfe, deshalb oeffnet der Knopf den Bestaetigungsdialog
                  // unten; das Loeschen laeuft erst dort.
                  { label: 'Delete', icon: <DeleteOutlineIcon />, onClick: () => setBulkDeleteConfirm(true) },
                ]}
                returnFocusRef={listenRef}
                mode={isMobile ? 'mobile' : 'inline'}
              />
            </Box>

            {/* Erstaufbau: Skelett statt Spinner (`anti-patterns.md` §11). */}
            {loading && !jeGeladen ? (
              <Box sx={{ mt: SP.card }}>
                <POInboxSkeleton />
              </Box>
            ) : isMobile ? (
              /* ── Mobile card list ── */
              displayedEmails.length === 0 ? (
                <EmptyState {...leerProps} />
              ) : (
                <Stack spacing={SP.tight} data-testid="liste-karte">
                  {displayedEmails.map(email => (
                    <InboxCard
                      key={email.id}
                      email={email}
                      onIgnore={handleIgnore}
                      onReprocess={handleReprocess}
                      poOptions={poOptions}
                      poLoading={poLoading}
                      poError={poError}
                      onRetryOptions={loadPoOptions}
                      onLinked={handleLinked}
                      processingId={processingId}
                      isSeen={seenIds.has(email.id)}
                      onSeen={markSeen}
                      auswahl={auswahl}
                    />
                  ))}
                </Stack>
              )
            ) : (
              /* ── Desktop table ── */
              <Box overflow="auto" aria-busy={loading} data-testid="liste-tabelle"
                aria-label={loading ? 'Loading messages' : undefined} sx={{ mt: SP.card }}>
                {/* 🔴 `table-layout: fixed` + Breiten aus EINER Quelle
                    (`PO_INBOX_COL`): sonst verteilt der Browser die Spalten nach
                    Inhalt, und der Leerzustand (`colSpan`-Zeile) schiebt sie
                    anders als die Datenzeilen — P33 maß 5 px Sprung. Dieselbe
                    Bauform wie `/rma/inbox` (`rmaInboxColumns.js`). */}
                <Table size="small" sx={{ tableLayout: 'fixed' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" onClick={handleKopfAuswahl} sx={AUSWAHLZELLE_SX}>
                        {/* 🔴 WCAG 2.4.7: ohne `:focus-within` ist die Kopf-
                            Checkbox mit `opacity: 0` UNSICHTBAR, aber in der
                            Tabreihenfolge. Hausform der Schwesterliste
                            (`RMAInbox.jsx`). */}
                        <Box sx={{ opacity: auswahl.active ? 1 : 0, transition: 'opacity 0.15s',
                          '&:focus-within': { opacity: 1 },
                          ...hoverOnly({ opacity: 1 }) }}>
                          <Tooltip title="Selects every email on this page">
                            <Checkbox size="small"
                              checked={auswahl.headerCheckbox.checked}
                              indeterminate={auswahl.headerCheckbox.indeterminate}
                              onChange={auswahl.headerCheckbox.onChange}
                              inputProps={auswahl.headerCheckbox.inputProps}
                              sx={{ p: 0.5 }} />
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ width: PO_INBOX_COL.time }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>Time</Typography>
                      </TableCell>
                      <TableCell sx={{ width: PO_INBOX_COL.from }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>From</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>Subject</Typography>
                      </TableCell>
                      <TableCell sx={{ width: PO_INBOX_COL.po }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>PO</Typography>
                      </TableCell>
                      <TableCell sx={{ width: PO_INBOX_COL.intent }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>Intent</Typography>
                      </TableCell>
                      {/* §1.5 — the column renders `action_taken`, so it is
                          labelled for what it shows, not "Status". */}
                      <TableCell sx={{ width: PO_INBOX_COL.action }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>Action</Typography>
                      </TableCell>
                      <TableCell sx={{ width: PO_INBOX_COL.actions }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayedEmails.map(email => (
                      <InboxTableRow
                        key={email.id}
                        email={email}
                        isSelected={auswahl.isSelected(email.id)}
                        onToggleSelect={auswahl.toggle}
                        selectionActive={auswahl.active}
                        onIgnore={handleIgnore}
                        onReprocess={handleReprocess}
                        poOptions={poOptions}
                        poLoading={poLoading}
                        poError={poError}
                        onRetryOptions={loadPoOptions}
                        processingId={processingId}
                        isSeen={seenIds.has(email.id)}
                        onSeen={markSeen}
                        onLinked={handleLinked}
                      />
                    ))}
                    {displayedEmails.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 0, border: 0 }}>
                          <EmptyState {...leerProps} />
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            )}

            {/* 🔴 Fusszeile (Z5) — EINE fuer beide Breiten, mit
                `stickyOnMobile={false}` (BS17-Opt-in, §2.1): ohne die Prop ist
                die Vorgabe `true`, und die schwebende Fusszeile deckt mobil die
                Zone ueber der Unterkante. Nur fuer die SERVERSEITIGEN Reiter —
                der `unread`-Reiter ist client-gefiltert und hat keine Seiten.
                🔴 `page` ist hier 0-basiert (MUI `TablePagination`),
                `state.page` ist 1-basiert (§3.1.1) — beide Umrechnungen stehen
                an DIESER Naht. 🔴 Die Marke steht nur MIT Inhalt (N8.7): der
                Baustein zeichnet erst ab `count > rowsPerPage`. */}
            {!istUnread && total > INBOX_SEITENGROESSE && (
              <Box data-testid="liste-fuss" sx={{ pt: SP.card }}>
                <TableFooterSimple
                  count={total}
                  page={page - 1}
                  rowsPerPage={INBOX_SEITENGROESSE}
                  onPageChange={p => set('page', p + 1)}
                  stickyOnMobile={false}
                  maxWidth="100%"
                />
              </Box>
            )}
          </ListToolbar>
        </Box>
      </Panel>

      {/* 🔴 §2.5: die Bestaetigung des Sammel-Loeschens. Der alte
          `InlineConfirmButton` in der Eigenbau-Leiste trug sie; `BulkActionBar`
          kennt keinen Bestaetigungsschritt, also oeffnet der gefoerderte
          „Delete"-Knopf diesen Dialog und das Loeschen laeuft erst hier. */}
      <Dialog open={bulkDeleteConfirm} onClose={() => setBulkDeleteConfirm(false)}>
        <DialogTitle>Delete {auswahl.count} messages?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            The selected messages are removed from the inbox. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteConfirm(false)}>Cancel</Button>
          <Button color="error" variant="contained"
            onClick={() => { setBulkDeleteConfirm(false); handleBulkDelete() }}>
            Delete now
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  )
}

/**
 * 🔴 G4b (§3.1.1/§4): die SEITENGROESSE des Beschaffungs-Posteingangs.
 *
 * Sie stand als Literal `50` an mehreren Stellen zusammen (`limit` im Abruf,
 * `limit` im Inline-Abruf von `handleCheckInbox`, `rowsPerPage` in der Fusszeile,
 * `pageSize` im `useListQuery`-Aufruf und in der Bedingung der Fusszeile). EIN
 * Name, alle Verbraucher — wer die Groesse senkt, verliert sonst die Fusszeile
 * fuer 26–50 Treffer, obwohl `TableFooterSimple` dort Inhalt haette.
 *
 * ⚠️ ABSICHTLICH am DATEIENDE: eine Einfuegung weiter oben verschoebe jede Zeile
 * darunter, und auf diese Datei zeigen Hunderte Zeilenverweise aus `docs/`.
 * `const` statt `function` ist unbedenklich: das Modul ist beim ersten Render
 * vollstaendig ausgewertet, es gibt keine TDZ.
 */
const INBOX_SEITENGROESSE = 50
